/* eslint-disable react/jsx-max-depth */
import {
  Button,
  Center,
  chakra,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { IoIosSearch } from 'react-icons/io'
import { IoClose } from 'react-icons/io5'
import { MdOutlineCreateNewFolder } from 'react-icons/md'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../../ui-kit'
import { FeaturesEnum } from '../../../../../utils/features'
import { secondaryFoldersPerPage } from '../../../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../../../utils/roles'
import { ACLPermissionEnum, MLSortDirection } from '../../../../constants'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import type {
  CopyMoveFolderSuccessResponse,
  CopyMoveMediaErrorResponse,
} from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import type { CopyMoveItems } from '../../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import CreateFolder from '../../NewButton/CreateFolder'
import Breadcrumbs from './Breadcrumbs'
import FolderElement from './FolderElement'

interface Props {
  operation: 'move' | 'copy'
  isOpen: boolean
  onClose(): void
}

const CopyMoveDrawer = ({
  operation,
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)
  const {
    isOpen: isOpenNewFolderModal,
    onOpen: onOpenNewFolderModal,
    onClose: onCloseNewFolderModal,
  } = useDisclosure()

  const {
    isOpen: isOpenMovePopup,
    onOpen: onOpenMovePopup,
    onClose: onCloseMovePopup,
  } = useDisclosure()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [
    {
      drawerSearchFilter,
      secondarySortDescriptors: { sortDirection },
      drawerFolderList,
      secondaryFolderPage,
      folderPath,
      drawerFolderPath,
      selectedFiles,
      selectedFolders,
      refetch,
      refetchDrawerFolders,
      loadingDrawerFolders,
      drawerFolderPermission,
      loadingDrawerFolderPermission,
      selectedBreadcrumbFolder,
    },
    dispatch,
  ] = useContext(MLContext)

  const { user } = useContext(UserContext)

  const [searchFilter, setSearchFilter] = useState<string>(drawerSearchFilter)

  const [includeFileVersions, setIncludeFileVersions] = useState<boolean>(false)

  const [operationPermissionErrorMessage, setOperationPermissionErrorMessage] =
    useState<string>('')

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const copyMoveMedia = async (): Promise<void | boolean> => {
    let isBreadcrumbFolderMoved = false
    const items = [
      ...selectedFolders.map(folder => ({
        id: folder.folderId,
        name: folder.name,
        status: 'indeterminate',
      })),
      ...selectedFiles.map(file => ({
        id: file.fileId,
        name: file.name,
        status: 'indeterminate',
      })),
    ] as CopyMoveItems[]

    for await (const selectedFolder of selectedFolders) {
      const requestData: Record<string, string | boolean> = {
        sourceFolderPath: selectedFolder.folderPath,
        destinationPath: drawerFolderPath,
      }

      if (operation === 'copy') {
        requestData.includeFileVersions = includeFileVersions
      }

      const res = (await request.post(
        `/api/v2/bulkJobs/${
          operation === 'copy' ? 'copyFolder' : 'moveFolder'
        }`,
        requestData,
      )) as CopyMoveFolderSuccessResponse | CopyMoveMediaErrorResponse

      if (request.error) {
        if ([400, 401, 403, 404].includes(response.status)) {
          const index = items.findIndex(
            item => item.id === selectedFolder.folderId,
          )
          if (index !== -1) {
            items[index].status = 'error'
            items[index].errorMessage = (
              res as CopyMoveMediaErrorResponse
            ).message
            dispatch({
              type:
                operation === 'copy'
                  ? MediaLibraryActions.SET_COPY_ITEMS
                  : MediaLibraryActions.SET_MOVE_ITEMS,
              payload: items,
            })
          }
        } else {
          const index = items.findIndex(
            item => item.id === selectedFolder.folderId,
          )
          if (index !== -1) {
            items[index].status = 'error'
            items[index].errorMessage =
              operation === 'copy'
                ? t(
                    'error_in_copying_folder',
                    'Error in copying folder, please try again after sometime',
                  )
                : t(
                    'error_in_moving_folder',
                    'Error in moving folder, please try again after sometime',
                  )
            dispatch({
              type:
                operation === 'copy'
                  ? MediaLibraryActions.SET_COPY_ITEMS
                  : MediaLibraryActions.SET_MOVE_ITEMS,
              payload: items,
            })
          }
        }
      } else {
        const index = items.findIndex(
          item => item.id === selectedFolder.folderId,
        )
        if (index !== -1) {
          if (
            selectedBreadcrumbFolder?.folderId === selectedFolder.folderId &&
            operation === 'move' &&
            selectedBreadcrumbFolder?.folderPath !== '/'
          ) {
            isBreadcrumbFolderMoved = true
          }

          items[index].status = 'success'
          dispatch({
            type:
              operation === 'copy'
                ? MediaLibraryActions.SET_COPY_ITEMS
                : MediaLibraryActions.SET_MOVE_ITEMS,
            payload: items,
          })
        }
      }
    }

    if (isBreadcrumbFolderMoved) {
      const folderName = selectedBreadcrumbFolder?.name
      let newFolderPath

      if (drawerFolderPath !== '/') {
        const folderPathArray = drawerFolderPath.split('/')
        folderPathArray.push(folderName as string)
        newFolderPath = folderPathArray.join('/')
      } else {
        newFolderPath = `/${folderName}`
      }

      history.replace(
        `/media-library/${Base64.encode(newFolderPath, true)}${
          queryFromUrl as string
        }`,
      )

      return true
    }

    for await (const selectedFile of selectedFiles) {
      const requestData: Record<string, string | boolean> = {
        sourceFilePath: selectedFile.filePath,
        destinationPath: drawerFolderPath,
      }

      if (operation === 'copy') {
        requestData.includeFileVersions = includeFileVersions
      }

      const res = (await request.post(
        `/api/v2/files/${operation}`,
        requestData,
      )) as null | CopyMoveMediaErrorResponse

      if (request.error) {
        if ([400, 401, 403, 404].includes(response.status)) {
          const index = items.findIndex(item => item.id === selectedFile.fileId)
          if (index !== -1) {
            items[index].status = 'error'
            items[index].errorMessage = res?.message
            dispatch({
              type:
                operation === 'copy'
                  ? MediaLibraryActions.SET_COPY_ITEMS
                  : MediaLibraryActions.SET_MOVE_ITEMS,
              payload: items,
            })
          }
        } else {
          const index = items.findIndex(item => item.id === selectedFile.fileId)
          if (index !== -1) {
            items[index].status = 'error'
            items[index].errorMessage =
              operation === 'copy'
                ? t(
                    'error_in_copying_file',
                    'Error in copying file, please try again after sometime',
                  )
                : t(
                    'error_in_moving_file',
                    'Error in moving file, please try again after sometime',
                  )
            dispatch({
              type:
                operation === 'copy'
                  ? MediaLibraryActions.SET_COPY_ITEMS
                  : MediaLibraryActions.SET_MOVE_ITEMS,
              payload: items,
            })
          }
        }
      } else {
        const index = items.findIndex(item => item.id === selectedFile.fileId)
        if (index !== -1) {
          items[index].status = 'success'
          dispatch({
            type:
              operation === 'copy'
                ? MediaLibraryActions.SET_COPY_ITEMS
                : MediaLibraryActions.SET_MOVE_ITEMS,
            payload: items,
          })
        }
      }
    }

    return isBreadcrumbFolderMoved
  }

  const resetSearch = useCallback(() => {
    dispatch([
      {
        type: MediaLibraryActions.RESET_DRAWER_SEARCH,
      },
      {
        type: MediaLibraryActions.SET_DRAWER_FOLDER,
        payload: '/',
      },
    ])
  }, [])
  const BreadcrumbReplacement = useMemo<React.ReactElement | null>(() => {
    if (drawerSearchFilter) {
      return (
        <HStack maxWidth="70%">
          <Text isTruncated color={colors.appSlate}>
            <chakra.span fontSize="14px">
              {t('search_results_for', 'Search results for ')}
            </chakra.span>
            <Tooltip label={drawerSearchFilter}>
              <chakra.span
                fontSize="14px"
                fontWeight="medium"
              >{`"${drawerSearchFilter}"`}</chakra.span>
            </Tooltip>
          </Text>
          <Spacer />
          <Icon
            onClick={resetSearch}
            cursor="pointer"
            as={IoClose}
            boxSize="1.5rem"
            color={colors.appBattleshipGrey}
          />
        </HStack>
      )
    }

    return null
  }, [drawerSearchFilter])

  useEffect(() => {
    setSearchFilter(drawerSearchFilter)
  }, [drawerSearchFilter])

  useEffect(() => {
    if (isRestrictedUser(user?.roles)) {
      if (drawerFolderPath !== '/') {
        operation === 'copy'
          ? setOperationPermissionErrorMessage(
              drawerFolderPermission === ACLPermissionEnum.CONTRIBUTE ||
                drawerFolderPermission === ACLPermissionEnum.MANAGE
                ? ''
                : 'notAllowed',
            )
          : setOperationPermissionErrorMessage(
              drawerFolderPermission === ACLPermissionEnum.MANAGE
                ? ''
                : 'notAllowed',
            )
      } else {
        setOperationPermissionErrorMessage('neverAllowed')
      }
    } else if (folderPath === drawerFolderPath) {
      setOperationPermissionErrorMessage('sameSourceAndDestination')
    } else {
      setOperationPermissionErrorMessage('')
    }
  }, [
    folderPath,
    user,
    drawerFolderPath,
    loadingDrawerFolderPermission,
    drawerFolderPermission,
    operation,
    isOpen,
  ])

  const getCopyMoveErrorMessage = (copyMoveError): string => {
    switch (copyMoveError) {
      case 'notAllowed':
        return t(
          'no_permission_error',
          'User does not have valid permission on destination path to perform operation',
        )

      case 'sameSourceAndDestination':
        return t(
          'same_source_and_destination',
          'Invalid operation as asset already exist on given path',
        )

      default:
        return t('invalid_operation', 'Invalid operation')
    }
  }

  return (
    <>
      <Drawer
        isOpen={isOpen}
        size="sm"
        onClose={() => {
          setOperationPermissionErrorMessage('')
          onCloseMovePopup()
          onClose()
        }}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {operation === 'move'
              ? t('move_to', 'Move To')
              : t('copy_to', 'Copy To')}
          </DrawerHeader>
          <Divider />

          <VStack
            width="full"
            spacing="1em"
            alignItems="start"
            padding="1em 1.5em"
          >
            <HStack width="full" spacing="1em">
              <HStack
                height="full"
                width="80%"
                spacing="0em"
                alignItems="stretch"
              >
                <Input
                  bgColor="rgba(237, 242, 247, 0.3)"
                  borderLeftRadius="5px"
                  borderRightRadius="0px"
                  borderWidth="0.5px"
                  borderColor="#cdd5df"
                  placeholder={t('folder_name', 'Folder name')}
                  value={searchFilter}
                  onChange={e => void setSearchFilter(e.target.value)}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      dispatch([
                        {
                          type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
                          payload: searchFilter,
                        },
                        {
                          type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
                          payload: searchFilter
                            ? `(name : "${searchFilter}")`
                            : '',
                        },
                      ])
                    }
                  }}
                  _active={{}}
                  _focus={{}}
                  _hover={{}}
                />

                <Center
                  height="full"
                  padding="0 0.5em"
                  borderWidth="0.5px"
                  borderColor="#cdd5df"
                  borderRadius="5px"
                  borderLeftRadius="0px"
                  borderLeft="none"
                  bgColor="white"
                >
                  <Icon as={IoIosSearch} w={6} h={6} color="#4a5568" />
                </Center>
              </HStack>
              <Spacer />
              {operationPermissionErrorMessage === 'neverAllowed' ? null : (
                <Tooltip
                  label={t(
                    'create_folder_permission_denied',
                    'User does not have valid permission to create folder.',
                  )}
                  isDisabled={
                    !(isRestrictedUser(user?.roles)
                      ? drawerFolderPermission !==
                          ACLPermissionEnum.CONTRIBUTE &&
                        drawerFolderPermission !== ACLPermissionEnum.MANAGE
                      : false)
                  }
                  shouldWrapChildren
                >
                  <IconButton
                    icon={<Icon as={MdOutlineCreateNewFolder} boxSize={8} />}
                    aria-label="Drawer New Folder Button"
                    color="appClearBlue"
                    backgroundColor="appPaleGrey"
                    boxShadow="none"
                    borderRadius="full"
                    marginRight="0.5em"
                    onClick={() => {
                      onOpenNewFolderModal()
                    }}
                    isLoading={loadingDrawerFolderPermission}
                    isDisabled={
                      isRestrictedUser(user?.roles)
                        ? drawerFolderPermission !==
                            ACLPermissionEnum.CONTRIBUTE &&
                          drawerFolderPermission !== ACLPermissionEnum.MANAGE
                        : false
                    }
                  />
                </Tooltip>
              )}
            </HStack>
            <HStack
              spacing="0.5em"
              width="full"
              justify="space-between"
              align="center"
            >
              {BreadcrumbReplacement ?? (
                <Breadcrumbs onCloseMovePopup={onCloseMovePopup} />
              )}

              <Select
                maxW="40%"
                variant="addon"
                value={sortDirection}
                onChange={e => {
                  dispatch({
                    type: MediaLibraryActions.SET_SECONDARY_SORT_DIRECTION,
                    payload: e.target.value as MLSortDirection,
                  })
                }}
              >
                <option value={MLSortDirection.ASC}>
                  {t('oldest_first', 'Oldest First')}
                </option>
                <option value={MLSortDirection.DESC}>
                  {t('newest_first', 'Newest First')}
                </option>
              </Select>
            </HStack>
          </VStack>
          <Divider />
          <DrawerBody>
            {loadingDrawerFolders ? (
              <Center height="100%">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Center>
            ) : (
              <>
                {drawerFolderList.map(folder => (
                  <FolderElement
                    folder={folder}
                    onCloseMovePopup={onCloseMovePopup}
                    key={folder.folderId}
                  />
                ))}
                {drawerFolderList.length !== 0 &&
                drawerFolderList.length % secondaryFoldersPerPage === 0 &&
                drawerFolderList.length / secondaryFoldersPerPage ===
                  secondaryFolderPage + 1 ? (
                  <Flex direction="row-reverse" padding="1em 0.75em 0 0">
                    <Button
                      as="a"
                      variant="link"
                      cursor="pointer"
                      color="appClearBlue"
                      onClick={() => {
                        dispatch({
                          type: MediaLibraryActions.LOAD_MORE_SECONDARY_FOLDERS,
                        })
                      }}
                    >
                      {t('view_more', 'View More')}
                    </Button>
                  </Flex>
                ) : null}
                {drawerFolderList.length === 0 && (
                  <Center padding="2em 0em">
                    <Text>{t('no_folders_found', 'No Folders Found')}</Text>
                  </Center>
                )}
              </>
            )}
          </DrawerBody>
          {!(operationPermissionErrorMessage === 'neverAllowed') &&
          !loadingDrawerFolders ? (
            isOpenMovePopup ? (
              <DrawerFooter>
                <VStack width="full" alignItems="flex-start">
                  <HStack
                    width="full"
                    justifyContent="space-evenly"
                    spacing="8"
                  >
                    <Button
                      variant="outline"
                      onClick={() => {
                        onCloseMovePopup()
                      }}
                      width="full"
                    >
                      {t('cancel', 'Cancel')}
                    </Button>
                    <Popover
                      returnFocusOnClose={false}
                      isOpen={isOpenMovePopup}
                      onClose={onCloseMovePopup}
                      placement="top"
                      closeOnBlur={false}
                    >
                      <PopoverTrigger>
                        <Button
                          width="full"
                          isDisabled={Boolean(
                            isSubmitting || operationPermissionErrorMessage,
                          )}
                          onClick={async () => {
                            setSubmitting(true)
                            onCloseMovePopup()
                            onClose()
                            const isBreadcrumbFolderMoved =
                              await copyMoveMedia()
                              await new Promise(resolve => setTimeout(resolve, 1500))
                            setSubmitting(false)
                            if (!isBreadcrumbFolderMoved) {
                              dispatch({
                                type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                              })
                              await refetch()
                              await refetchDrawerFolders()
                            }
                          }}
                        >
                          {t('confirm_move', 'Confirm')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent mr="1em">
                        <PopoverArrow />
                        <PopoverBody>
                        {
                          <>
                            <Text>
                              {
                                user?.pricingPlan.featuresList.includes(
                                  FeaturesEnum.PUBLIC_LINKS,
                                ) ? t(
                                  'move_confirm_body_acl',
                                  'Please confirm as after moving the assets, users with whom these assets are shared may lose access.',
                                ) : null
                              }
                            </Text>
                            <Text>
                              {
                                user?.pricingPlan.featuresList.includes(
                                  FeaturesEnum.ACL_AND_COLLECTIONS,
                                ) ? t(
                                  'move_confirm_body_public_links',
                                  'Public links for this asset and all assets that fall under it will stop working',
                                ) : null
                              }
                            </Text>
                          </>
                        }
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                </VStack>
              </DrawerFooter>
            ) : (
              <DrawerFooter>
                <VStack width="full" alignItems="flex-start">
                  {operation === 'copy' ? (
                    <Flex width="full">
                      <Checkbox
                        isChecked={includeFileVersions}
                        onChange={e =>
                          void setIncludeFileVersions(e.target.checked)
                        }
                      >
                        Include File Versions
                      </Checkbox>
                    </Flex>
                  ) : null}
                  <HStack
                    width="full"
                    justifyContent="space-evenly"
                    spacing="8"
                  >
                    <Button
                      variant="outline"
                      onClick={() => {
                        setOperationPermissionErrorMessage('')
                        onClose()
                      }}
                      width="full"
                    >
                      {t('cancel', 'Cancel')}
                    </Button>
                    <Tooltip
                      label={getCopyMoveErrorMessage(
                        operationPermissionErrorMessage,
                      )}
                      shouldWrapChildren
                      isDisabled={Boolean(
                        !(isSubmitting || operationPermissionErrorMessage),
                      )}
                    >
                      <Button
                        width="11.5em"
                        isDisabled={Boolean(
                          isSubmitting || operationPermissionErrorMessage,
                        )}
                        onClick={async () => {
                          if (
                            operation === 'move' &&
                            (
                              user?.pricingPlan.featuresList.includes(
                                FeaturesEnum.ACL_AND_COLLECTIONS,
                              ) ||
                              user?.pricingPlan.featuresList.includes(
                                FeaturesEnum.PUBLIC_LINKS,
                              )  
                            )
                          ) {
                            onOpenMovePopup()
                            return
                          }
                          setSubmitting(true)
                          onClose()
                          const isBreadcrumbFolderMoved = await copyMoveMedia()
                          setSubmitting(false)

                          if (!isBreadcrumbFolderMoved) {
                            dispatch({
                              type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                            })
                            await new Promise(resolve => setTimeout(resolve, 1500))
                            if (operation === 'move') {
                              await refetch()
                            }
                            await refetchDrawerFolders()
                          }
                        }}
                      >
                        {operation === 'move'
                          ? t('move_here', 'Move Here')
                          : t('copy_here', 'Copy Here')}
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>
              </DrawerFooter>
            )
          ) : null}
        </DrawerContent>
      </Drawer>
      <CreateFolder
        isOpen={isOpenNewFolderModal}
        isSecondaryLocation={true}
        onClose={onCloseNewFolderModal}
      />
    </>
  )
}

export default CopyMoveDrawer
